/**
 * Easter icons — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterIconsPack: ToilePack = {
    slug: 'easter-icons',
    category: 'season',
    name: 'Easter icons',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'egg', label: 'Egg icon', subject: 'A painted egg reduced to one simple glyph, built to read small, isolated on a plain ground'},
        {key: 'bunny', label: 'Bunny icon', subject: 'A bunny head with long ears as one simple glyph, built to read small, isolated'},
        {key: 'chick', label: 'Chick icon', subject: 'A chick as one simple glyph, built to read small, isolated'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['a tulip icon instead', 'as a rounded app icon', 'with a thicker glyph'],
    directionHint: 'try: a tulip icon instead',
    seo: {
        title: 'Easter Icons: Egg, Bunny & Chick Glyphs',
        metaDescription: 'Nine Easter icons — egg, bunny, chick — built to read at 32 pixels, in pastel kawaii, vintage, and modern minimal styles.',
        h1: 'Easter icon set: egg, bunny, chick glyphs',
        tagline: 'Nine icons, three glyphs, three treatments each.',
        intro: 'Nine Easter glyph icons — a painted egg, a bunny head with long ears, and a chick — each reduced to one simple shape and built to stay legible at small icon size, in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Set against a plain background with no lettering, ready to drop into an app, website, or slide deck.',
        useCases: [
            'an app or website seasonal icon set',
            'a slide deck or infographic bullet icon',
            'an invitation or menu section marker',
            'a printable label or tag accent',
        ],
        faq: [
            {q: 'Do these icons stay legible at small sizes?', a: 'Yes — each glyph is simplified down to one clear shape specifically so it holds up at roughly 32 pixels, not just at full size.'},
            {q: 'Are the icons a matched set or three unrelated styles?', a: 'Each of the three subjects — egg, bunny, chick — comes in the same three styles, so you can pick one register and use all three glyphs together as a set.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I recolor the icons to match my brand?', a: 'The pastel and modern styles are simple enough to recolor in most image editors; the vintage style is more textured and recolors less cleanly.'},
        ],
    },
}
